import { z } from 'zod';
import { SportNameSchema } from './profile.js';
import type { SportName } from './profile.js';
import type { TaxMode } from './tax.js';

/**
 * F12 venue-pricing types + Zod schemas.
 *
 * Decisions: see plans/12_F12_venue_profile_self_service.md (Decision 3).
 *
 * One row per `(sub_venue × sport × day-of-week × time-band)` pricing band.
 * The editor PUT-replaces all bands for a `(sub_venue_id, sport_name)` pair
 * in one transaction — no partial diffs. Overlap detection runs server-side
 * by sorting bands by `start_minute` within each `(sub_venue, sport, dow)`
 * group; the first overlap returns 422 PRICING_BAND_OVERLAP with
 * `conflictingBandIndex` so the editor can highlight the offending row.
 *
 * Time-of-day is stored as minute-of-day (0..1440):
 *   - `start_minute=420` = 07:00
 *   - `end_minute=720`   = 12:00
 *   - `end_minute=1440`  = "until midnight"
 * `end_minute` MUST be strictly greater than `start_minute`. Bands shorter
 * than 30 minutes are rejected client-side (the F02 `DayTimeBandSelector`
 * enforces a 30-min minimum) and server-side via Zod.
 *
 * `priceAmount` is USD minor units (integer cents) per the F05 convention.
 * Tax columns are inherited from the parent venue at row creation; owners
 * may override per-band (e.g. tax-free promotional hour).
 */

// ─── Constants ───────────────────────────────────────────────────────────────

export const VENUE_PRICING_MIN_BAND_MINUTES = 30;
export const VENUE_PRICING_DAY_MINUTES = 1440;

// ─── Raw DB row shape ────────────────────────────────────────────────────────

export type VenuePricingBand = {
  readonly id: string;
  readonly subVenueId: string;
  readonly sportName: SportName;
  readonly dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  readonly startMinute: number;
  readonly endMinute: number;
  readonly priceAmount: number;
  readonly priceCurrency: string;
  readonly taxMode: TaxMode;
  readonly taxRatePercent: string; // numeric driver returns string
  readonly label: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

// ─── Zod schemas ─────────────────────────────────────────────────────────────

/**
 * One pricing band payload (no id — the API rewrites all bands for the
 * `(sub_venue, sport)` pair from scratch on each PUT). `sportName` is
 * omitted from the band shape — the route path (`/sub-venues/:id/sports/
 * :sportName/pricing`) carries it so each band inherits.
 */
export const VenuePricingBandInputSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6),
    startMinute: z
      .number()
      .int()
      .min(0)
      .max(VENUE_PRICING_DAY_MINUTES - 1),
    endMinute: z.number().int().min(1).max(VENUE_PRICING_DAY_MINUTES),
    priceAmount: z.number().int().nonnegative(),
    priceCurrency: z.string().length(3).default('USD'),
    taxMode: z.enum(['inclusive', 'exclusive', 'none']).optional(),
    taxRatePercent: z
      .string()
      .regex(/^\d{1,3}(\.\d{1,2})?$/)
      .optional(),
    label: z.string().max(40).nullable().optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.endMinute <= val.startMinute) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endMinute must be strictly greater than startMinute',
        path: ['endMinute'],
      });
    }
    if (val.endMinute - val.startMinute < VENUE_PRICING_MIN_BAND_MINUTES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `band must span at least ${VENUE_PRICING_MIN_BAND_MINUTES} minutes`,
        path: ['endMinute'],
      });
    }
  });
export type VenuePricingBandInput = z.infer<typeof VenuePricingBandInputSchema>;

/**
 * Full replace payload for a `(sub_venue, sport)` pair. Empty array clears all
 * bands for that pair (the editor offers "Remove all pricing" as an action).
 */
export const ReplaceVenuePricingInputSchema = z
  .object({
    sportName: SportNameSchema,
    bands: z.array(VenuePricingBandInputSchema).max(168), // 7 days × 24 hours of 1-hour bands
  })
  .strict();
export type ReplaceVenuePricingInput = z.infer<typeof ReplaceVenuePricingInputSchema>;
